import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DynamicProfileVisualizer } from "../Components/DynamicQuestionnaire/client";
import { ChevronLeft, Download, Share } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * DynamicBrandPage Component
 *
 * A direct access page for viewing the DynamicProfileVisualizer
 * Can be accessed with an optional appended name: /brand-page/:appendedName
 * Uses a Firestore mapping to find the corresponding responseId
 */
const DynamicBrandPage = () => {
  const { appendedName } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("Brand Profile");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responseId, setResponseId] = useState(null);

  // Load responseId from URL mapping
  useEffect(() => {
    const fetchResponseId = async () => {
      try {
        setLoading(true);

        if (!appendedName) {
          setError("No URL identifier provided");
          setLoading(false);
          return;
        }

        // Get the URL mapping from Firestore
        const urlMappingRef = doc(db, "urlMappings", appendedName);
        const urlMappingDoc = await getDoc(urlMappingRef);

        if (!urlMappingDoc.exists()) {
          setError("This brand profile URL does not exist");
          setLoading(false);
          return;
        }

        // Set the responseId from the mapping
        const data = urlMappingDoc.data();
        setResponseId(data.responseId);

        // Set the title from custom name if available
        if (data.customName) {
          setTitle(data.customName);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching response ID:", err);
        setError("Failed to load the brand profile");
        setLoading(false);
      }
    };

    fetchResponseId();
  }, [appendedName]);

  // Set page title based on appended name
  useEffect(() => {
    if (appendedName && title !== "Brand Profile") {
      document.title = `${title} | Brand Profile`;
    } else if (appendedName) {
      // Convert URL format to display format (replace hyphens with spaces and capitalize)
      const formattedName = appendedName
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      setTitle(formattedName);
      document.title = `${formattedName} | Brand Profile`;
    } else {
      document.title = "Brand Profile";
    }
  }, [appendedName, title]);

  const goBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Function to share the URL
  const shareUrl = () => {
    if (navigator.share) {
      navigator
        .share({
          title: document.title,
          url: window.location.href,
        })
        .catch((err) => {
          console.error("Error sharing:", err);
        });
    } else {
      // Fallback for browsers that don't support navigator.share
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          // You could show a toast notification here
          alert("URL copied to clipboard!");
        })
        .catch((err) => {
          console.error("Error copying to clipboard:", err);
        });
    }
  };

  // Function to download as PDF (placeholder)
  const downloadAsPdf = () => {
    alert("PDF download functionality would be implemented here");
    // This would require a library like html2pdf or a server-side solution
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen w-full bg-gray-900">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Show error message
  if (error || !responseId) {
    return (
      <div className="flex justify-center items-center min-h-screen w-full bg-gray-900">
        <div className="bg-red-500/10 p-6 rounded-xl border border-red-500/20 max-w-lg">
          <h2 className="text-xl font-bold text-red-500 mb-2">
            {error || "Could not find this brand profile"}
          </h2>
          <p className="text-white/70 mb-4">
            The URL you've entered doesn't match any existing brand profiles.
          </p>
          <button
            onClick={goBack}
            className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded text-white"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dynamic-brand-page bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-900/80 backdrop-blur-md border-b border-gray-800 shadow-md">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={goBack}
            className="flex items-center text-white hover:text-blue-400 transition-colors"
          >
            <ChevronLeft size={20} className="mr-1" />
            Back
          </button>

          <div className="flex gap-2">
            <button
              onClick={shareUrl}
              className="flex items-center px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm"
            >
              <Share size={16} className="mr-1" />
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Content - Direct render of the DynamicProfileVisualizer */}
      <DynamicProfileVisualizer responseId={responseId} />
    </div>
  );
};

export default DynamicBrandPage;
